import BehaviourGraph from "./BehaviourGraph";
import CustomerKPIComparisonGraph from "./CustomerKPIComparisonGraph";
import { OrdersIcon, VolumeIcon, PricingIcon, ProfitIcon } from "./icons";

import { useAccDetStatistics } from "@/api/account/useAccDetStatistics";
import { useState } from "react";
import { SkeletonCard, StatisticsTabSkeleton } from "./skeleton/AccountDetailsSkeleton";

const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(parseFloat(value))) {
      return "0.00";
    }
    return parseFloat(value).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
}

const StatisticsTab = ({leadID, companyName}) => {
    const {statCard, behaviourData, boxPlotsGrossVol, boxPlotsMargin, boxPlotsSpread, boxPlotsNetPNL, loading, error} = useAccDetStatistics({leadID});
    // Add state for selected metric
    const [selectedMetric, setSelectedMetric] = useState("Volume");
    // Function to get the appropriate data based on the selected metric
    const getMetricData = () =>{
        switch (selectedMetric) {
            case "Volume":
                return boxPlotsGrossVol;
            case "Margin":
                return boxPlotsMargin;
            case "Spread":
                return boxPlotsSpread;
            case  "Net PNL":
                return boxPlotsNetPNL;
            default:
                return boxPlotsGrossVol;
        }
    };

    const clearFilter = () => {
        setSelectedMetric("Volume");
    };

    // Loading State
    if (loading) {
        return <StatisticsTabSkeleton />;
    }

    // Error/Empty State
    if (error || !statCard) {
        return (
            <div className="w-full flex flex-col shadow-lg p-4 bg-white rounded-lg items-center justify-center min-h-[400px]">
                <div className="flex flex-col items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-16 h-16 text-gray-400 mb-4">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" />
                    </svg>
                    <h2 className="text-2xl font-bold text-gray-700 mb-2">No Statistics Available</h2>
                    <p className="text-gray-500 text-center">
                        {error ? 'Unable to load statistics data. Please try again later.' : 'No statistical data found for this account.'}
                    </p>
                </div>
            </div>
        );
    }

    return(
        <div className="w-full flex flex-col shadow-lg p-4 md:p-6 bg-white rounded-2xl md:rounded-3xl item-start">
            <div className="flex flex-col items-center mb-4 border-b border-gray-200">
                <h2 className="text-lg md:text-xl font-bold mb-4 break-words text-center">{companyName}</h2>
            </div>
            <h2 className="py-2 font-bold mb-4 text-base md:text-xl">User Statistics</h2>
            <div className="flex flex-col md:flex-row md:justify-between gap-4 mb-4">
                {/* Volume */}
                <div className="p-4 bg-white border rounded-lg w-full md:w-1/4">
                    <div className = "flex flex-row items-center justify-between mb-4 ">
                        <div className="flex items-center gap-2">
                            <img src={VolumeIcon} alt="Volume Icon" className="w-6 h-6"/>
                            <h2 className="text-xl text-center"><b>Volume</b></h2>
                        </div>
                        <span className = "h-[15px] flex text-center rounded-full bg-[#FFEDE9] border border-[#DD3730] text-[#DD3730] text-xs px-2">
                            <a>Low</a>
                        </span>
                    </div>
                    <div className="mb-4">
                        <p className="font-bold text-base">Average Gross Volume</p>
                        <a className="text-sm">MYR {formatCurrency(statCard?.avg_transaction_size)}</a>
                    </div>
                    <div className="mb-4">
                        <p className="font-bold text-base">Total Gross Volume</p>
                        <a className="text-sm">MYR {formatCurrency(statCard?.total_gross_volume_myr)}</a>
                    </div>
                    <div>
                        <p className="font-bold text-base">Last Transaction Date</p>
                        <a className="text-sm">{statCard?.last_transaction_datetime}</a>
                    </div>
                </div>
                {/* Pricing */}
                <div className="p-4 bg-white border rounded-lg w-full md:w-1/4">
                    <div className = "flex flex-row items-center justify-between mb-4 ">
                        <div className="flex items-center gap-2">
                            <img src={PricingIcon} alt="Pricing Icon" className="w-6 h-6"/>
                            <h2 className="text-xl text-center"><b>Pricing</b></h2>
                        </div>
                        <span className = "h-[15px] flex text-center rounded-full bg-[#FFEDE9] border border-[#DD3730] text-[#DD3730] text-xs px-2">
                            <a>Low</a>
                        </span>
                    </div>
                    <div className="mb-4">
                        <p className="font-bold text-base">Price Sensitivity</p>
                        <a className="text-sm">{statCard?.price_sensitivity}%</a>
                    </div>
                    <div className="mb-4">
                        <p className="font-bold text-base">Discount Margin</p>
                        <a className="text-sm">{statCard?.discount_margin}%</a>
                    </div>
                    <div>
                        <p className="font-bold text-base">Total Discount</p>
                        <a className="text-sm">MYR {formatCurrency(statCard?.total_discount)}</a>
                    </div>
                </div>
                {/* Profit */}
                <div className="p-4 bg-white border rounded-lg w-full md:w-1/4">
                    <div className = "flex flex-row items-center justify-between mb-4 ">
                        <div className="flex items-center gap-2">
                            <img src={ProfitIcon} alt="Profit Icon" className="w-6 h-6"/>
                            <h2 className="text-xl text-center"><b>Profit</b></h2>
                        </div>
                        <span className = "h-4 flex text-center rounded-full bg-[#E3F3FF] border border-[#29B6F6] text-[#29B6F6] text-xs px-2">
                            <a>Medium</a>
                        </span>
                    </div>
                    <div className="mb-4">
                        <p className="font-bold text-base">Average Net Profit Margin</p>
                        <a className="text-sm">{statCard?.avg_net_profit_margin}%</a>
                    </div>
                    <div className="mb-4">
                        <p className="font-bold text-base">Average Net Profit</p>
                        <a className="text-sm">MYR {formatCurrency(statCard?.avg_net_pnl)}</a>
                    </div>
                    <div>
                        <p className="font-bold text-base">Lifetime Value</p>
                        <a className="text-sm">MYR {formatCurrency(statCard?.lifetime_value)}</a>
                    </div>
                </div>
                {/* Orders */}
                <div className="p-4 bg-white border rounded-lg w-full md:w-1/4">
                    <div className = "flex flex-row items-center justify-between mb-4 ">
                        <div className="flex items-center gap-2">
                            <img src={OrdersIcon} alt="Order Icon" className="w-6 h-6"/>
                            <h2 className="text-xl text-center"><b>Orders</b></h2>
                        </div>
                        <span className = "h-4 flex text-center rounded-full bg-[#E3F3FF] border border-[#29B6F6] text-[#29B6F6] text-xs px-2">
                            <a>Medium</a>
                        </span>
                    </div>
                    <div className="mb-4">
                        <p className="font-bold text-base">Transaction Interval</p>
                        <a className="text-sm">{statCard?.transaction_interval} Days</a>
                    </div>
                    <div className="mb-4">
                        <p className="font-bold text-base">Deposit Time</p>
                        <a className="text-sm">{statCard?.deposit_time} hours</a>
                    </div>
                    <div>
                        <p className="font-bold text-base">Total Orders</p>
                        <a className="text-sm">{statCard?.total_orders} orders</a>
                    </div>
                </div>
            </div>
            <div className="mb-4">
                <h2 className="py-2 font-bold mb-4 text-base md:text-xl">Customer KPI Comparison</h2>
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 mb-4">
                    <span className="font-bold text-[#94A3B8] p-2 text-sm md:text-base">Select Metric:</span>
                    <div className="relative">
                        <select
                            value={selectedMetric}
                            onChange={(e) => setSelectedMetric(e.target.value)}
                            className=" font-bold
                                        block appearance-none w-full 
                                        bg-white hover:border-gray-500 text-gray-700 
                                        py-2 px-4 pr-8 rounded
                                        leading-tight focus:outline-none 
                                        focus: shadow-outline"
                            
                        >
                            <option value="Volume">Volume</option>
                            <option value="Margin">Margin</option>
                            <option value="Spread">Spread</option>
                            <option value="Net PNL">Net PnL</option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-geay-700">
                            <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                                <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
                            </svg>
                        </div>
                    </div>
                    <button
                        onClick={clearFilter}
                        className="underline font-bold text-sm md:text-base"
                    >
                        Clear Filters
                    </button>
                </div>
                <div className="p-4 w-full rounded-lg bg-[#E2E8F0] border-[#E2E8F0]">
                    <CustomerKPIComparisonGraph 
                        selectedMetric={selectedMetric}
                        boxPlotsData = {getMetricData()}
                    />
                </div>
            </div>
            <div className="mb-4">
                <h2 className="py-2 font-bold mb-4 text-base md:text-xl">Behaviour</h2>
                <div className="w-full rounded-lg bg-[#E2E8F0] border-[#E2E8F0]">
                    <BehaviourGraph behaviourData={behaviourData} />
                </div>
            </div>
        </div>
    )
}
export default StatisticsTab;